import React, { useRef, useState, useLayoutEffect } from "react";
import { nestSheet, SHEET_W, SHEET_H, SHEET_MARGIN } from "../lib/nesting";

// Pixel-accurate, true-to-scale rendering of stickers nested on an 8.5x11 sheet.
export default function SheetView({ stickerSrc, isTransparent, footW, footH, gap }) {
  const pageRef = useRef(null);
  const [pw, setPw] = useState(0);

  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setPw(entries[0].contentRect.width));
    ro.observe(el);
    setPw(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const g = Number(gap);

  // Sticker footprint = the size the customer selected (incl. cut border).
  // Sheet-cut nests by this so stickers pack tight with just the gap between them.
  const vw = Number(footW);
  const vh = Number(footH);

  const nest = nestSheet(vw, vh, gap);
  const cols = Math.max(nest.cols, 1);
  const rows = Math.max(nest.rows, 1);

  // Footprint of one sticker on the page (inches), in the chosen orientation.
  const sw = nest.rot ? vh : vw;
  const sh = nest.rot ? vw : vh;

  const ppi = pw / SHEET_W; // px per inch (page width represents 8.5in)

  const blockW = cols * sw + (cols - 1) * g;
  const blockH = rows * sh + (rows - 1) * g;
  const originX = Math.max((SHEET_W - blockW) / 2, SHEET_MARGIN);
  const originY = Math.max((SHEET_H - blockH) / 2, SHEET_MARGIN);

  const cellW = sw * ppi;
  const cellH = sh * ppi;
  // Image element size in its natural (unrotated) orientation = fills the cell.
  const imgW = vw * ppi;
  const imgH = vh * ppi;

  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({ x: (originX + c * (sw + g)) * ppi, y: (originY + r * (sh + g)) * ppi });
    }
  }

  return (
    <div data-testid="sticker-stage" className="relative w-full h-[460px] sm:h-[540px] rounded-2xl overflow-hidden flex items-center justify-center macbook-surface">
      <div className="flex items-center justify-center w-full h-full p-8">
        <div
          ref={pageRef}
          className="relative backing-liner rounded-md nb-border nb-shadow-sm overflow-hidden"
          style={{ height: "min(90%, 540px)", aspectRatio: `${SHEET_W / SHEET_H}` }}
          data-testid="sheet-page"
        >
          {ppi > 0 &&
            cells.map((cell, i) => (
              <div key={i} className="absolute" style={{ left: `${cell.x}px`, top: `${cell.y}px`, width: `${cellW}px`, height: `${cellH}px` }}>
                <img
                  src={stickerSrc}
                  alt=""
                  className="absolute top-1/2 left-1/2"
                  style={{
                    width: `${imgW}px`,
                    height: `${imgH}px`,
                    maxWidth: "none",
                    maxHeight: "none",
                    objectFit: "contain",
                    transform: `translate(-50%, -50%) rotate(${nest.rot ? 90 : 0}deg)`,
                    mixBlendMode: isTransparent ? "multiply" : "normal",
                  }}
                />
              </div>
            ))}
        </div>
      </div>
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur nb-border rounded-full px-3 py-1 text-xs font-head font-bold">
        Sheet cut · {nest.n} per 8.5"×11" sheet
      </div>
    </div>
  );
}
